import React, { useState, useMemo, useEffect, useCallback } from "react"
import * as Icons from "lucide-react"
import { emailApi } from "../services/emailApi"

const AVATAR_COLORS = [
  "#2563eb",
  "#059669",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#db2777",
]
const colorFor = name =>
  AVATAR_COLORS[(name || "?").charCodeAt(0) % AVATAR_COLORS.length]
const initials = name =>
  (name || "?")
    .split(" ")
    .map(p => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()

// Address Book — synced live from the connected Exchange mailbox. With no
// search term it lists the personal Contacts folder; typing a search also
// reaches the organization's Global Address List (EWS has no API to list the
// full GAL without downloading the binary Offline Address Book, so directory
// entries surface via search — same as Outlook's address book search box).
export default function EmailPeople({ activeAccount, onComposeTo }) {
  const [contacts, setContacts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState(null)

  const accountId = activeAccount?.account_id
  const isEws = (activeAccount?.protocol || "").toLowerCase() === "ews"

  const load = useCallback(
    search => {
      if (!accountId || !isEws) return
      setLoading(true)
      setError(null)
      emailApi
        .getAddressBookContacts({
          account: accountId,
          search: search || undefined,
        })
        .then(res => {
          setContacts(res.data?.contacts || [])
        })
        .catch(() => {
          setError("Could not load the address book from your mailbox.")
        })
        .finally(() => setLoading(false))
    },
    [accountId, isEws]
  )

  useEffect(() => {
    load()
  }, [load])

  // Debounce directory search (min 2 chars) so the GAL is queried as-you-type.
  useEffect(() => {
    const q = query.trim()
    if (q.length < 2) return undefined
    const t = setTimeout(() => load(q), 350)
    return () => clearTimeout(t)
  }, [query, load])

  const filtered = useMemo(() => {
    return [...contacts].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    )
  }, [contacts])

  const active = contacts.find(c => c.id === selected)

  if (!accountId) {
    return (
      <div className="em-people em-people-empty-state">
        <Icons.Users size={40} strokeWidth={1} />
        <p>No mailbox selected</p>
      </div>
    )
  }

  if (!isEws) {
    return (
      <div className="em-people em-people-empty-state">
        <Icons.BookUser size={40} strokeWidth={1} />
        <p>Address book sync isn't available for this mailbox</p>
        <span>
          Connect an Exchange (EWS) account to sync your address book.
        </span>
      </div>
    )
  }

  return (
    <div className="em-people">
      <div className="em-people-list">
        <div className="em-people-toolbar">
          <button
            className="em-sidebar-newmail em-people-new"
            onClick={() => load(query.trim())}
            disabled={loading}
          >
            <Icons.RefreshCw size={16} className={loading ? "em-spin" : ""} />{" "}
            <span>{loading ? "Syncing…" : "Sync"}</span>
          </button>
          <div className="em-people-search">
            <Icons.Search size={14} />
            <input
              placeholder="Search address book"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="em-people-scroll">
          {error && (
            <div className="em-people-empty">
              <Icons.AlertTriangle size={40} strokeWidth={1} />
              <p>{error}</p>
              <button
                className="em-search-clear-btn"
                onClick={() => load(query.trim())}
              >
                Try again
              </button>
            </div>
          )}
          {!error && !loading && filtered.length === 0 && (
            <div className="em-people-empty">
              <Icons.Users size={40} strokeWidth={1} />
              <p>
                {query.trim()
                  ? "No matches"
                  : "No personal contacts synced yet"}
              </p>
              <span>
                {query.trim()
                  ? "Try a different name or email"
                  : "Type a name or email above to search your organization directory"}
              </span>
            </div>
          )}
          {filtered.map(c => (
            <div
              key={c.id}
              className={`em-people-item ${
                selected === c.id ? "em-people-item-active" : ""
              }`}
              onClick={() => setSelected(c.id)}
            >
              <span
                className="em-people-avatar"
                style={{ background: colorFor(c.name) }}
              >
                {initials(c.name)}
              </span>
              <div className="em-people-item-info">
                <div className="em-people-item-name">
                  {c.name || "(No name)"}
                </div>
                <div className="em-people-item-email">{c.title || c.email}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="em-people-detail">
        {!active ? (
          <div className="em-viewer-empty-content em-people-detail-empty">
            <Icons.UserCircle size={56} strokeWidth={1} />
            <h3>Select a contact</h3>
            <p>Choose someone from the list to see their details</p>
          </div>
        ) : (
          <div className="em-people-card">
            <div className="em-people-card-header">
              <span
                className="em-people-card-avatar"
                style={{ background: colorFor(active.name) }}
              >
                {initials(active.name)}
              </span>
              <div>
                <h2>{active.name}</h2>
                {active.title && (
                  <div className="em-people-card-title">
                    {active.title}
                    {active.company ? ` · ${active.company}` : ""}
                  </div>
                )}
              </div>
              {active.email && (
                <div className="em-people-card-actions">
                  <button
                    type="button"
                    title="Send email"
                    onClick={() => onComposeTo?.(active.email, active.name)}
                  >
                    <Icons.Mail size={16} />
                  </button>
                </div>
              )}
            </div>
            <div className="em-people-card-body">
              {active.email && (
                <div className="em-people-card-row">
                  <Icons.Mail size={16} />
                  <button
                    type="button"
                    className="em-people-link-btn"
                    onClick={() => onComposeTo?.(active.email, active.name)}
                  >
                    {active.email}
                  </button>
                </div>
              )}
              {active.business_phone && (
                <div className="em-people-card-row">
                  <Icons.Phone size={16} />
                  <span>
                    {active.business_phone}{" "}
                    <span className="em-muted">(business)</span>
                  </span>
                </div>
              )}
              {active.mobile_phone && (
                <div className="em-people-card-row">
                  <Icons.Smartphone size={16} />
                  <span>
                    {active.mobile_phone}{" "}
                    <span className="em-muted">(mobile)</span>
                  </span>
                </div>
              )}
              {active.company && (
                <div className="em-people-card-row">
                  <Icons.Building2 size={16} />
                  <span>{active.company}</span>
                </div>
              )}
              {active.department && (
                <div className="em-people-card-row">
                  <Icons.Briefcase size={16} />
                  <span>{active.department}</span>
                </div>
              )}
              {active.location && (
                <div className="em-people-card-row">
                  <Icons.MapPin size={16} />
                  <span>{active.location}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
